import { getTimezone } from 'reducers/utils';
import {
  all,
  call,
  cancel,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import API from 'services/api';
import {
  patrolDataGridActions,
  patrolFilterActions,
  patrolPageActions,
  patrolTasksNS,
  patrolTaskSelectors,
} from 'containers/Pages/Tasks/Patrols';
import get from 'lodash/get';
import {
  closeDetails,
  detailOpenState,
  modalState,
  openDetails,
  setLoading,
  showSnackbar,
  toggleModal,
} from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { TASK_MODEL } from 'services/entity/task';
import { getUserSelectedLanguage } from 'reducers/users';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import {
  getPatrolFirstLocation,
  getPatrolOtherLocations,
  getPatrolTemplateDetailRaw,
} from 'containers/Pages/Tasks/PatrolTemplates/Selectors/patrolTemplateSelectors';
import isEmpty from 'lodash/isEmpty';
import { PATROL_STATUS } from 'utils/constants/patrol';

function getEvidenceFromTask(apiData, id) {
  if (apiData && apiData.data && apiData.data.tasks) {
    const data = apiData.data.tasks.find(item => item.id === id);
    const evidence = data[TASK_MODEL.evidence.id];
    return evidence;
  }
  return [];
}
// Takes a location from a patrol and if the task id is not null go and retrieve the task
// Gets all the evidence items in the task and adds them to the list
function* extractEvidence(location, completedEvidencesMap) {
  if (location && location.taskId) {
    const locationEvidence = yield call(API.fetchTaskDetails, {
      id: location.taskId,
    });
    if (locationEvidence && locationEvidence.data) {
      const evidence = getEvidenceFromTask(
        locationEvidence.data,
        location.taskId,
      );
      if (evidence) {
        completedEvidencesMap.push({
          templateId: location.definedLocation.taskId,
          outsideTaskId: location.taskId,
          completedEvidences: evidence,
        });
      }
    }
  }
}

export function* fetchPatrolTaskListSaga({ payload }) {
  try {
    const { filters, from } = payload;
    const timezone = yield select(getTimezone);
    const { data } = yield call(API.fetchPatrolList, {
      ...filters,
      from,
      timezone,
    });
    if (data.data != null) {
      const totalEntries = get(data, 'meta.total');
      yield put(
        patrolDataGridActions.setGridList({ list: data.data, totalEntries }),
      );

      yield put(patrolFilterActions.setFilterKey(false));
      yield put(patrolDataGridActions.setForceRefresh(false));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchPatrolTaskDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const { data } = yield call(API.fetchPatrolReportDetail, id);
    // fetch patrol def details
    if (data.data !== null) {
      const patrolDefID = data.data?.patrolDefinition?.id;
      yield put(
        patrolTemplateActions.fetchGridRowDetails({
          id: patrolDefID,
          patrolEvidenceList: true,
        }),
      );
    }

    if (data) {
      const d = data.data;
      const ls = d.locations;
      const completedEvidencesMap = [];
      //get any evidence on the first location
      yield call(extractEvidence, d.firstLocation, completedEvidencesMap);
      //get any evidence on the other locations
      yield all(
        ls.map(l => {
          if (l.taskId !== null && l.taskId !== undefined) {
            return extractEvidence(l, completedEvidencesMap);
          }
          return null;
        }),
      );
      yield put(
        patrolDataGridActions.setGridRowDetails({
          details: {
            ...data.data,
            completedEvidencesMap, // completed evidences
          },
        }),
      );
      yield put(openDetails(patrolTasksNS));
    }
    yield put(setLoading(false));
  } catch (err) {
    console.log('err: ', err);
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* printPatrolDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchPatrolDetailPdf, {
      id,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* printPatrolListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchPatrolListPdf, {
      ...filters,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* postEvidenceTaskSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { task } = payload;
    // upload the task
    const { data } = yield call(API.taskUpload, {
      data: {
        ...task,
      },
    });
    const getPatrolTaskDetails = yield select(
      patrolTaskSelectors.getPatrolTaskDetailsRaw,
    );
    if (data && !isEmpty(getPatrolTaskDetails)) {
      yield call(fetchPatrolTaskDetailsSaga, {
        payload: {
          id: getPatrolTaskDetails.id,
        },
      });
    } else {
      // it is opened for the first time and patroling is done
      const firstLoc = yield select(getPatrolFirstLocation);
      const otherLocs = yield select(getPatrolOtherLocations);
      const patrolTemplateDetailRaw = yield select(getPatrolTemplateDetailRaw);
      const updatedTaskID = data.data.tasks[0].templateId;

      const isLocFoundinFirst = firstLoc.taskId === updatedTaskID;

      const isLocFoundInOtherLoc = otherLocs.find(
        f => f.taskId === updatedTaskID,
      );
      const updateEvidence = data.data.tasks[0].evidence;
      if (isLocFoundinFirst) {
        // update loc evidence to complete

        const updateFirstLocation = {
          ...firstLoc,
          evidence: updateEvidence,
          outsideTaskId: data.data.tasks[0].id,
        };
        yield put(
          patrolTemplateActions.setGridRowDetails({
            details: {
              ...patrolTemplateDetailRaw,
              firstLocation: updateFirstLocation,
            },
          }),
        );
      }
      // if return to start is true then first loc copy exists in otherLoc
      // check if the first Loc is completed, if it is not completed then do not go in this condition
      if (isLocFoundInOtherLoc && firstLoc.evidence[0].completed) {
        // update loc evidence to complete
        const updateOtherLocations = otherLocs.map(m => {
          if (m.taskId === updatedTaskID) {
            return {
              ...m,
              evidence: updateEvidence,
              outsideTaskId: data.data.tasks[0].id,
            };
          }
          return m;
        });
        yield put(
          patrolTemplateActions.setGridRowDetails({
            details: {
              ...patrolTemplateDetailRaw,
              locations: updateOtherLocations,
            },
          }),
        );
      }
    }
    yield put(setLoading(false));
  } catch (err) {
    console.log('err: ', err);
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* abortPatrolTaskSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    if (id) {
      const { data } = yield call(API.abortVirtualPatrol, id);

      const modalStateObject = yield select(modalState);
      const detailpanelOpen = yield select(detailOpenState);
      if (modalStateObject[patrolTasksNS]) {
        // only toggle modal, if it is open
        yield put(toggleModal(patrolTasksNS));
        yield put(patrolTemplateActions.setGridRowDetails({ details: {} }));
        yield put(patrolDataGridActions.setGridRowDetails({ details: {} }));
      }
      // only fetch if the detail panel is open
      if (
        data.data.currentStatus === PATROL_STATUS.RESOLVED_ABORTED.id &&
        detailpanelOpen[patrolTasksNS]
      ) {
        yield call(fetchPatrolTaskDetailsSaga, {
          payload: {
            id,
          },
        });
      }
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* startPatrolSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const {
      virtualPatrolInfo,
      newTaskUUID,
      statusAudit,
      taskSuccessful,
    } = payload;

    const { data } = yield call(API.startVirtualPatrol, virtualPatrolInfo);

    const firstLoc = yield select(getPatrolFirstLocation);
    const uploadPayload = {
      ...data.data,
      firstLocation: {
        ...virtualPatrolInfo.firstLocation,
        definedLocation: {
          checkPointSequence: 1,
          qrCode: firstLoc.qrCode,
          taskId: firstLoc.taskId,
          timestamp: data.data.startTime,
          point: firstLoc.point,
        },
        qrCode: firstLoc.qrCode,
        userPoint: firstLoc.point,
        timestamp: data.data.startTime,
        taskId: newTaskUUID,
        ...(newTaskUUID && taskSuccessful),
      },
      patroller: virtualPatrolInfo.patroller,
      ...((statusAudit.status === PATROL_STATUS.RESOLVED_SUCCESSFUL.id ||
        statusAudit.status === PATROL_STATUS.RESOLVED_ABORTED.id ||
        statusAudit.status === PATROL_STATUS.RESOLVED_INCOMPLETE.id) && {
        currentStatus: statusAudit.status,
        statusAudit: [statusAudit, ...data.data.statusAudit], // the order is important. Latest status should be on the top
      }),
    };

    if (data.data) {
      const startPatrolData = yield call(
        API.uploadVirtualPatrol,
        uploadPayload,
      );
      if (startPatrolData.data?.data) {
        yield put(
          patrolDataGridActions.setGridRowDetails({
            details: startPatrolData.data.data,
          }),
        );
      }
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* uploadPatrolSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { transformPatrolObject } = payload;
    const { data } = yield call(API.uploadVirtualPatrol, transformPatrolObject);
    if (data.data) {
      yield put(
        patrolDataGridActions.setGridRowDetails({ details: data.data }),
      );
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* patrolTaskSagas() {
  yield takeLatest(
    patrolDataGridActions.fetchGridList,
    fetchPatrolTaskListSaga,
  );
  yield takeLatest(patrolPageActions.printDetails, printPatrolDetailsSaga);
  yield takeLatest(patrolPageActions.printList, printPatrolListSaga);
  yield takeLatest(patrolPageActions.postEvidenceTask, postEvidenceTaskSaga);
  yield takeLatest(patrolPageActions.abortPatrolTask, abortPatrolTaskSaga);
  yield takeLatest(patrolPageActions.startVirtualPatrol, startPatrolSaga);
  yield takeLatest(patrolPageActions.uploadVirtualPatrol, uploadPatrolSaga);
  while (true) {
    const allTasksSaga = yield all([
      takeLatest(
        patrolDataGridActions.fetchGridRowDetails,
        fetchPatrolTaskDetailsSaga,
      ),
    ]);
    yield take('STOP'); // if the create patrol form is opened, stop the above sagas from running -> close detail panel ->clear all the data
    yield all(allTasksSaga.map(task => cancel(task)));
    yield put(closeDetails(patrolTasksNS));
    yield put(patrolTemplateActions.setGridRowDetails({ details: {} }));
    yield put(patrolDataGridActions.setGridRowDetails({ details: {} }));
  }
}
